import { api } from "api/apiService";

const usersBaseUrl = "/users";

// --- Керування користувачами ---

/**
 * Завантажує список всіх користувачів.
 * Відповідає ендпоінту: GET /users
 * @returns {Promise<Array>} Проміс, що повертає масив об'єктів користувачів.
 */
export const fetchUsers = () => {
  return api.get(usersBaseUrl);
};

/**
 * Створює (реєструє) нового користувача.
 * Відповідає ендпоінту: POST /users
 * @param {object} userData - Дані нового користувача (username, email, password, role_id).
 * @returns {Promise<object>} Проміс, що повертає об'єкт зі створеним користувачем.
 */
export const registerUser = (userData) => {
  return api.post(usersBaseUrl, userData);
};

/**
 * Оновлює роль для конкретного користувача.
 * Відповідає ендпоінту: PUT /users/:id/role
 * @param {number|string} userId - ID користувача, роль якого оновлюється.
 * @param {number} newRoleId - ID нової ролі.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateUserRole = (userId, newRoleId) => {
  const url = `${usersBaseUrl}/${encodeURIComponent(userId)}/role`;
  const body = { role_id: newRoleId };
  return api.put(url, body);
};

/**
 * Видаляє користувача за його ID.
 * Відповідає ендпоінту: DELETE /users/:id
 * @param {number|string} userId - ID користувача, якого потрібно видалити.
 * @returns {Promise<void>} Проміс, що завершується після успішного видалення.
 */
export const deleteUser = (userId) => {
  const url = `${usersBaseUrl}/${encodeURIComponent(userId)}`;
  return api.delete(url);
};

// --- Керування ролями та дозволами ---

/**
 * Завантажує список всіх доступних ролей.
 * Відповідає ендпоінту: GET /users/roles
 * @returns {Promise<Array>} Проміс, що повертає масив об'єктів ролей.
 */
export const fetchRoles = () => {
  return api.get(`${usersBaseUrl}/roles`);
};

/**
 * Створює нову роль.
 * Відповідає ендпоінту: POST /users/roles
 * @param {object} roleData - Дані нової ролі ({ name, description }).
 * @returns {Promise<object>} Проміс, що повертає об'єкт новоствореної ролі.
 */
export const createRole = (roleData) => {
  const url = `${usersBaseUrl}/roles`;
  return api.post(url, roleData);
};

/**
 * Видаляє роль за її ID.
 * Відповідає ендпоінту: DELETE /users/roles/:id
 * @param {number|string} roleId - ID ролі, яку потрібно видалити.
 * @returns {Promise<void>} Проміс, що завершується після успішного видалення.
 */
export const deleteRole = (roleId) => {
  const url = `${usersBaseUrl}/roles/${encodeURIComponent(roleId)}`;
  return api.delete(url);
};

/**
 * Завантажує список всіх доступних дозволів (permissions).
 * Відповідає ендпоінту: GET /users/permissions
 * @returns {Promise<Array>} Проміс, що повертає масив об'єктів дозволів.
 */
export const fetchPermissions = () => {
  return api.get(`${usersBaseUrl}/permissions`);
};

/**
 * Оновлює дозволи для конкретної ролі.
 * Відповідає ендпоінту: PUT /users/roles/:id/permissions
 * @param {number|string} roleId - ID ролі, для якої оновлюються дозволи.
 * @param {Array<number>} permissionIds - Масив ID нових дозволів для цієї ролі.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateRolePermissions = (roleId, permissionIds) => {
  const url = `${usersBaseUrl}/roles/${encodeURIComponent(roleId)}/permissions`;
  const body = { permission_ids: permissionIds };
  return api.put(url, body);
};

// --- Менеджер CRM (зв'язок із VMS) ---

/**
 * Завантажує менеджерів CRM для випадного списку «Менеджер CRM».
 * Відповідає ендпоінту: GET /users/crm-managers
 * @returns {Promise<Array>} Проміс, що повертає масив { id, full_name, email, status }.
 */
export const fetchCrmManagers = () => {
  return api.get(`${usersBaseUrl}/crm-managers`);
};

/**
 * Прив'язує користувача до менеджера CRM (null — відв'язує).
 * Відповідає ендпоінту: PUT /users/:id/crm-manager
 * @param {number|string} userId - ID користувача.
 * @param {number|null} crmManagerId - ID менеджера в CRM або null.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateUserCrmManager = (userId, crmManagerId) => {
  const url = `${usersBaseUrl}/${encodeURIComponent(userId)}/crm-manager`;
  return api.put(url, { crm_manager_id: crmManagerId });
};

// --- Тімлід (команди для VMS) ---

/**
 * Призначає користувачу тімліда — іншого користувача ЦАУ (null — без команди).
 * Відповідає ендпоінту: PUT /users/:id/team-lead
 * @param {number|string} userId - ID користувача.
 * @param {number|null} teamLeadId - ID користувача-тімліда або null.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateUserTeamLead = (userId, teamLeadId) => {
  const url = `${usersBaseUrl}/${encodeURIComponent(userId)}/team-lead`;
  return api.put(url, { team_lead_id: teamLeadId });
};
